import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
  useEffect,
  useCallback,
  useSyncExternalStore,
} from 'react'
import { useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { sacrificeApi } from '../config/sacrifice-api'
import type { FullProject } from 'sacrifice-utils/api'
import type { NetworkDetails, SacrificeInfo } from 'sacrifice-utils/types'
import { useAuth } from '.'
import {
  applyProjectOverrides,
  subscribeToOverrides,
  getOverrides,
} from '../utils/project-overrides'
import { getProjectIdFromPath } from '../utils/active-project'

export interface ProjectContextValue {
  projectId: string | null
  projects: FullProject[]
  selectedProject: FullProject | null
  projectNetworks: NetworkDetails[]
  userAddresses: SacrificeInfo[] | null
  isLoading: boolean
  isLoadingProjects: boolean
  isLoadingNetworks: boolean
  isLoadingUserAddresses: boolean
  error: string | null
  /**
   * Whether the sacrifice period has ended for the selected project.
   * Returns true when the current time is past the project's endTime.
   * Returns false if the project has no endTime (sacrifice never ends).
   */
  hasSacrificeEnded: boolean
  setProjectId: (projectId: string | null) => void
  refreshProjects: () => Promise<void>
  refreshNetworks: () => Promise<void>
  refreshUserAddresses: () => Promise<void>
}

export const ProjectContext = createContext<ProjectContextValue | undefined>(undefined)

export const useProjects = (): ProjectContextValue => {
  const context = useContext(ProjectContext)
  if (context === undefined) {
    throw new Error('useProjects must be used within a ProjectProvider')
  }
  return context
}

interface ProjectProviderProps {
  children: ReactNode
}

// Snapshot function for useSyncExternalStore - returns a stable reference when overrides haven't changed
const getOverridesSnapshot = () => JSON.stringify(getOverrides())

export const ProjectProvider = ({ children }: ProjectProviderProps) => {
  const location = useLocation()
  const [projectId, setProjectId] = useState<string | null>(null)
  const { authData, isSignedIn } = useAuth()

  // Subscribe to project overrides changes (for testing/debugging from console)
  const overridesSnapshot = useSyncExternalStore(subscribeToOverrides, getOverridesSnapshot)

  // Fetch projects with React Query - needed early for routing
  const {
    data: rawProjects = [],
    isLoading: isLoadingProjects,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: ['projects'],
    queryFn: ({ signal }) => sacrificeApi.projects.getAll({ signal }),
    staleTime: 60 * 1000, // 1 minute - data is considered fresh for 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes - cache garbage collection time
    refetchOnWindowFocus: false, // Don't refetch on window focus
    placeholderData: [], // Provide empty array as placeholder
  })

  // Apply any overrides to projects (for testing/debugging from console)
  // overridesSnapshot is referenced to trigger re-computation when overrides change
  const projects = useMemo(() => {
    void overridesSnapshot // Signal dependency for re-computation
    return applyProjectOverrides(rawProjects)
  }, [rawProjects, overridesSnapshot])

  // Auto-sync projectId from URL parameters
  // For routes like /sacrifice/{project_id}/...
  const urlProjectId = useMemo(() => getProjectIdFromPath(location.pathname), [location.pathname])

  // Sync URL project ID with context state when projects are available
  useEffect(() => {
    if (urlProjectId && projects.length > 0) {
      setProjectId(urlProjectId)
    }
  }, [urlProjectId, projects])

  const selectedProject = useMemo(
    () => projects?.find(p => p.projectId === projectId) ?? null,
    [projects, projectId]
  )

  /**
   * Determines if the sacrifice period has ended based on the project's endTime.
   * Uses a state + effect pattern to re-evaluate when time passes the end threshold.
   */
  const [hasSacrificeEnded, setHasSacrificeEnded] = useState(false)

  const checkSacrificeEnded = useCallback(() => {
    if (!selectedProject?.endTime) {
      return false
    }
    const endTimestamp = new Date(selectedProject.endTime).getTime()
    return Date.now() >= endTimestamp
  }, [selectedProject?.endTime])

  // Update hasSacrificeEnded state when project changes or when end time passes
  useEffect(() => {
    // Initial check
    setHasSacrificeEnded(checkSacrificeEnded())

    // If no end time or already ended, no need to set up interval
    if (!selectedProject?.endTime) {
      return
    }

    const endTimestamp = new Date(selectedProject.endTime).getTime()
    const now = Date.now()

    // If already past end time, we're done
    if (now >= endTimestamp) {
      return
    }

    // Calculate time until end
    const msUntilEnd = endTimestamp - now

    // If end time is within the next hour, check every second
    // Otherwise, check every minute until we're close
    const checkInterval = msUntilEnd < 60 * 60 * 1000 ? 1000 : 60 * 1000

    const interval = setInterval(() => {
      const ended = checkSacrificeEnded()
      setHasSacrificeEnded(ended)
      if (ended) {
        clearInterval(interval)
      }
    }, checkInterval)

    return () => clearInterval(interval)
  }, [selectedProject?.endTime, checkSacrificeEnded])

  // Fetch project networks when projectId is set
  // Defer until after critical path completes
  const {
    data: projectNetworks = [],
    isLoading: isLoadingNetworks,
    error: networksError,
    refetch: refetchNetworks,
  } = useQuery({
    queryKey: ['projectNetworks', projectId],
    queryFn: async ({ signal }) => {
      return await sacrificeApi.projects.getNetworks({ projectId: projectId!, signal })
    },
    enabled: !!projectId, // Only fetch when projectId is set
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  })

  const refreshProjects = async () => {
    await refetch()
  }

  const refreshNetworks = async () => {
    await refetchNetworks()
  }

  // Fetch user addresses for the current project
  const {
    data: userAddressesResponse = null,
    isLoading: isLoadingUserAddresses,
    error: userAddressesError,
    refetch: refetchUserAddresses,
  } = useQuery({
    queryKey: [
      'userAddresses',
      projectId,
      authData?.signature,
      authData?.inputs.timestamp?.toString(),
    ],
    queryFn: async ({ signal }) => {
      if (!authData || !projectId) return null
      console.log('🔄 Fetching user addresses for project', projectId)
      return await sacrificeApi.addresses
        .all({
          inputs: { timestamp: authData.inputs.timestamp },
          projectId,
          signature: authData.signature,
          signal,
        })
        .catch(() => null)
    },
    enabled: !!authData && !!projectId && isSignedIn,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    refetchOnWindowFocus: false,
  })

  const refreshUserAddresses = async () => {
    await refetchUserAddresses()
  }

  // Aggregate loading state
  const isLoading = isLoadingProjects || isLoadingNetworks || isLoadingUserAddresses

  const value: ProjectContextValue = {
    projectId,
    setProjectId,
    projects,
    selectedProject,
    projectNetworks,
    userAddresses: userAddressesResponse?.addresses ?? null,
    isLoading,
    isLoadingProjects,
    isLoadingNetworks,
    isLoadingUserAddresses,
    hasSacrificeEnded,
    error: queryError
      ? (queryError as Error).message
      : networksError
        ? (networksError as Error).message
        : userAddressesError
          ? (userAddressesError as Error).message
          : null,
    refreshProjects,
    refreshNetworks,
    refreshUserAddresses,
  }

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>
}
